import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllProductCate } from "api/ProductCategoryApi";
import { getAllProductSubCate } from "api/ProductSubCateApi";
import SEO from "./Seo";

export default function CategorysMenu({ isMobile = false, onClose }) {
  const [categories, setCategories] = useState([]);
  const [hoveredType, setHoveredType] = useState(null);
  const [productSubCate, setProductSubCate] = useState([]);
  const [subCateMap, setSubCateMap] = useState({});
  const [expandedMobile, setExpandedMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount(window.innerWidth));
  const history = useNavigate();

  function getVisibleCount(width) {
    if (width >= 1400) return 10;
    if (width >= 1200) return 8;
    if (width >= 1024) return 6;
    if (width >= 992)  return 5;
    return 10;
  }

  useEffect(() => {
    loadCategories();
    const handleResize = () => {
      const w = window.innerWidth;
      setIsDesktop(w >= 992);
      setVisibleCount(getVisibleCount(w));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    //eslint-disable-next-line
  }, []);

  const loadCategories = () => {
    getAllProductCate({}).then((res) => {
      const visible = (res.response || []).filter(
        (item) =>
          item.visibilityMode === "WEB_MOBILE" ||
          item.visibilityMode === "MOBILE"
      );
      setCategories(visible);
      preloadAllSubCate(visible);
    });
  };

  const preloadAllSubCate = async (cats) => {
    try {
      const results = await Promise.all(
        cats.map((cat) =>
          getAllProductSubCate({ metaProdCateEnty: cat?.id })
            .then((data) => ({ id: cat.id, list: data.response || [] }))
            .catch(() => ({ id: cat.id, list: [] }))
        )
      );
      const map = {};
      results.forEach((r) => { map[r.id] = r.list; });
      setSubCateMap(map);
    } catch (error) {
      console.error("Error preloading subcategories:", error);
    }
  };

  const getProductSub = async (category) => {
    try {
      const module = await import("api/ProductSubCateApi");
      const data = await module.getAllProductSubCate({ metaProdCateEnty: category?.id });
      const list = data.response || [];
      setProductSubCate(list);
      setSubCateMap((prev) => ({ ...prev, [category?.id]: list }));
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const handleCategoryHover = (category) => {
    if (category !== null) {
      if (subCateMap[category.id]) {
        setProductSubCate(subCateMap[category.id]);
      } else {
        getProductSub(category);
      }
    }
    setHoveredType(category);
  };

  const goToList = (cat) => {
    history("/ProductList", { state: { category: cat } });
    if (onClose) onClose();
  };

  const goToSubCategory = (subCat, parentCat) => {
    history("/ProductList", { state: { cat: subCat, category: parentCat } });
    if (onClose) onClose();
  };

  // ── MOBILE LAYOUT ──────────────────────────────────────────
  if (isMobile) {
    return (
      <nav className="ej-mobile-nav" aria-label="Mobile navigation" style={{ padding: "8px 16px" }}>
        <div className="ej-mobile-nav-item">
          <button
            className={`ej-mobile-nav-link${expandedMobile ? " ej-mobile-nav-link--active" : ""}`}
            onClick={() => setExpandedMobile((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: "12px 16px",
              background: "#faf3d8",
              border: "none",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#b8860b",
              cursor: "pointer"
            }}
          >
            <span>Browse Categories</span>
            <svg
              width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ transform: expandedMobile ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {expandedMobile && (
            <div style={{ marginTop: "8px", padding: "0 8px" }}>
              <button
                style={{ width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: "13px", fontWeight: 500, color: "#1c1c1e", borderBottom: "1px solid #f0e8d0", cursor: "pointer" }}
                onClick={() => { history("/ProductList"); onClose?.(); }}
              >
                All Products
              </button>
              {categories.map((cat, i) => (
                <div key={i}>
                  <button
                    style={{ width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: "13px", fontWeight: 500, color: "#1c1c1e", borderBottom: "1px solid #f0e8d0", cursor: "pointer" }}
                    onClick={() => { goToList(cat); }}
                  >
                    {cat.categoryName}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f0e8d0", display: "flex", flexDirection: "column", gap: "12px" }}>
          <a href="/Offers" style={{ textDecoration: "none", fontSize: "13px", color: "#1c1c1e", padding: "8px 16px" }} onClick={onClose}>Offers</a>
          <a href="/Contact" style={{ textDecoration: "none", fontSize: "13px", color: "#1c1c1e", padding: "8px 16px" }} onClick={onClose}>Contact</a>
        </div>
      </nav>
    );
  }

  // ── DESKTOP LAYOUT — Horizontal nav bar below header with dropdown ────────
  return (
    <>
      <SEO title="Zinger Jewellers" description="Premium jewellery collection." name="Zinger Jewellers" type="article" />

      {/* Nav bar strip */}
      <div className="ej-catbar-root">
        <nav className="ej-catbar-nav" aria-label="Category navigation">
          {categories.slice(0, visibleCount).map((cat, i) => (
            <div
              key={i}
              className="ej-catbar-item"
              onMouseEnter={() => handleCategoryHover(cat)}
              onMouseLeave={() => setHoveredType(null)}
            >
              <button
                className={`ej-catbar-btn${hoveredType === cat ? " ej-catbar-btn--active" : ""}`}
                onClick={() => goToList(cat)}
              >
                {cat.categoryName}
              </button>

              {/* Subcategory dropdown */}
              {hoveredType === cat && isDesktop && (
                <div
                  className="ej-catbar-dropdown"
                  style={i >= Math.floor(visibleCount / 2) ? { left: "auto", right: 0 } : {}}
                  onMouseEnter={() => handleCategoryHover(cat)}
                  onMouseLeave={() => setHoveredType(null)}
                >
                  <div className="ej-catbar-drop-header">
                    <span className="ej-catbar-drop-title">SHOP BY SUBCATEGORY</span>
                    <button className="ej-catbar-drop-viewall" onClick={() => goToList(cat)}>
                      View All {cat.categoryName} →
                    </button>
                  </div>
                  <div className="ej-catbar-drop-grid">
                    {productSubCate.length > 0 ? (
                      productSubCate.map((sub) => (
                        <button
                          key={sub.id}
                          className="ej-catbar-sub-btn"
                          onClick={() => goToSubCategory(sub, cat)}
                        >
                          {sub.subCategoryName}
                        </button>
                      ))
                    ) : (
                      <p className="ej-catbar-sub-empty">Loading subcategories…</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>

      <style>{`
        /* ── Root bar ── */
        .ej-catbar-root {
          width: 100%;
          background: #fff;
          display: flex;
          justify-content: center;
          overflow: visible;
        }

        /* ── Nav row ── */
        .ej-catbar-nav {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: nowrap;
          gap: 0;
          max-width: 1440px;
          width: 100%;
          overflow: visible;
        }

        /* ── Each item wrapper ── */
        .ej-catbar-item {
          position: relative;
          flex-shrink: 0;
        }

        /* ── Nav tab button ── */
        .ej-catbar-btn {
          display: block;
          padding: 14px 12px;
          font-family: inherit;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #3a3a3a;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          white-space: nowrap;
          transition: color 0.18s, border-color 0.18s;
        }
        @media (min-width: 1200px) {
          .ej-catbar-btn {
            padding: 14px 20px;
            font-size: 13px;
            letter-spacing: 0.06em;
          }
        }
        .ej-catbar-btn:hover,
        .ej-catbar-btn--active {
          color: #b8860b;
          border-bottom-color: #b8860b;
        }

        /* ── Dropdown panel ── */
        .ej-catbar-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 520px;
          max-width: 860px;
          width: 60vw;
          background: #fff;
          border: 1px solid #ede8d8;
          border-top: 2px solid #b8860b;
          border-radius: 0 0 14px 14px;
          box-shadow: 0 16px 40px rgba(0,0,0,0.12);
          padding: 20px 24px 24px;
          z-index: 1100;
          animation: ejDropSlide 0.16s ease;
        }

        @keyframes ejDropSlide {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Dropdown header row ── */
        .ej-catbar-drop-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f0e8d0;
        }
        .ej-catbar-drop-title {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          color: #b8860b;
          text-transform: uppercase;
        }
        .ej-catbar-drop-viewall {
          font-size: 12px;
          font-weight: 600;
          color: #b8860b;
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        /* ── Subcategory grid ── */
        .ej-catbar-drop-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        }

        .ej-catbar-sub-btn {
          text-align: left;
          padding: 10px 16px;
          font-size: 13px;
          font-weight: 500;
          color: #2c2c2e;
          background: #fafafa;
          border: 1px solid #ece8dc;
          border-radius: 8px;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.15s ease;
        }
        .ej-catbar-sub-btn:hover {
          background: #faf3d8;
          color: #b8860b;
          border-color: #d4aa5a;
        }

        .ej-catbar-sub-empty {
          font-size: 13px;
          color: #aaa;
          grid-column: 1 / -1;
          text-align: center;
          padding: 16px;
          margin: 0;
        }

        /* ── Hide on mobile only ── */
        @media (max-width: 600px) {
          .ej-catbar-root {
            display: none !important;
          }
        }
        /* ── Tablet: allow horizontal scroll for catbar ── */
        @media (min-width: 601px) and (max-width: 991px) {
          .ej-catbar-nav {
            justify-content: flex-start;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .ej-catbar-nav::-webkit-scrollbar { display: none; }
          .ej-catbar-btn {
            padding: 12px 10px;
            font-size: 11px;
          }
        }
      `}</style>
    </>
  );
}